<template>
  <main class="wallet-user-guide-page">
    <AppNavBar :title="t('wallet.userGuide')" background-color="#ffffff" />

    <section class="user-guide-content">
      <van-tabs
        v-model:active="activeTab"
        class="user-guide-tabs"
        :line-width="0"
        :animated="false"
      >
        <van-tab
          v-for="tab in tabs"
          :key="tab.key"
          :name="tab.key"
          :title="tab.label"
        >
          <section class="user-guide-list">
            <article
              v-for="item in guideItems[tab.key]"
              :key="item.image"
              :class="[
                'user-guide-card',
                { 'is-clickable': item.to || item.action },
              ]"
              :role="item.to || item.action ? 'button' : undefined"
              :tabindex="item.to || item.action ? 0 : undefined"
              @click="openGuide(item)"
              @keydown.enter="openGuide(item)"
            >
              <app-image
                :src="item.image"
                alt=""
                class="user-guide-card__image"
              />
              <span class="user-guide-card__title">{{ item.title }}</span>
              <span class="user-guide-card__arrow" aria-hidden="true">
                <app-image :src="arrowRightIcon" alt="" size="10" />
              </span>
            </article>
          </section>
        </van-tab>
      </van-tabs>
    </section>

    <p class="user-guide-footer">{{ t("wallet.userGuideFooter") }}</p>
  </main>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { refDoc } from "@/api/wallet";
import AppNavBar from "@/components/AppNavBar.vue";
import { useProfileStore } from "@/stores/profile";
import { useWebModalStore } from "@/stores/web-modal";
import { getRouteState, saveRouteState } from "@/utils/route-state";
import accountGuideImage from "@/assets/images/guide-account.png";
import cardGuideImage from "@/assets/images/guide-card.png";
import cardGuideImage2 from "@/assets/images/guide-card2.png";
import cardGuideImage3 from "@/assets/images/guide-card3.png";
import rewardGuideImage from "@/assets/images/guide-reward.png";
import arrowRightIcon from "@/assets/images/SVG/icon/arrow-right.svg";

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const webModalStore = useWebModalStore();
const guideRouteState =
  window.history.state?.__routeStateNamespace === "wallet-user-guide"
    ? getRouteState(route, "wallet-user-guide", {})
    : {};
const activeTab = ref(
  ["account", "card", "reward"].includes(guideRouteState.activeTab)
    ? guideRouteState.activeTab
    : "account",
);
const openingReferralDocument = ref(false);

watch(activeTab, (tab) => {
  const stateKey = saveRouteState("wallet-user-guide", {
    activeTab: tab,
  });
  window.history.replaceState(
    {
      ...(window.history.state || {}),
      __routeStateKey: stateKey,
      __routeStateNamespace: "wallet-user-guide",
    },
    "",
  );
});

const tabs = computed(() => [
  { key: "account", label: t("wallet.userGuideAccountTab") },
  { key: "card", label: t("wallet.userGuideCardTab") },
  { key: "reward", label: t("wallet.userGuideRewardTab") },
]);

const guideItems = computed(() => ({
  account: [
    {
      image: accountGuideImage,
      title: t("wallet.userGuideAccountItem"),
      to: "/wallet/user-guide/account",
    },
  ],
  card: [
    {
      image: cardGuideImage,
      title: t("wallet.userGuideCardItem"),
      to: "/wallet/user-guide/card",
    },
    {
      image: cardGuideImage2,
      title: t("wallet.userGuideCard2Item"),
      to: "/wallet/user-guide/card-physical",
    },
    {
      image: cardGuideImage3,
      title: t("wallet.userGuideCard3Item"),
      to: "/wallet/user-guide/card-security",
    },
  ],
  reward: [
    {
      image: rewardGuideImage,
      title: t("wallet.userGuideRewardItem"),
      action: "referral-document",
    },
  ],
}));

async function openGuide(item) {
  if (item.action === "referral-document") {
    await openReferralDocument();
    return;
  }

  if (item.to) {
    router.push(item.to);
  }
}

async function openReferralDocument() {
  if (openingReferralDocument.value) {
    return;
  }

  const address = profileStore.platformAddress || null;
  if (!address) {
    showFailToast(t("common.retryLater"));
    return;
  }

  openingReferralDocument.value = true;
  try {
    const result = await refDoc(
      {
        address,
        lang: locale.value || profileStore.locale || "zh-CN",
      },
      { meta: { showLoading: true } },
    );
    const url = String(result?.url || "").trim();
    if (!url) {
      showFailToast(t("common.retryLater"));
      return;
    }

    webModalStore.open({
      url,
      title: t("invite.pointsRules"),
      navBackgroundColor: "#fff",
    });
  } catch (error) {
    showFailToast(error?.message || t("common.retryLater"));
  } finally {
    openingReferralDocument.value = false;
  }
}
</script>

<style scoped lang="scss">
.wallet-user-guide-page {
  display: flex;
  flex-direction: column;
  min-height: var(--app-viewport-height);
  background: #ffffff;
  .van-nav-bar__title {
    color: #000;
  }
}

.user-guide-content {
  flex: 1;
  padding: 10px 16px 0;
}

.user-guide-tabs {
  :deep(.van-tabs__wrap) {
    overflow: visible;
    padding: 3px;
    border-radius: 30px;
    background: #e9e9e9;
  }

  :deep(.van-tabs__nav) {
    display: flex;
    background: transparent;
  }

  :deep(.van-tab) {
    flex: 1;
    padding: 3px 14px;
    border-radius: 30px;
    color: #222222;
    font-size: 13px;
  }

  :deep(.van-tab--active) {
    background: #ffffff;
    font-weight: 500;
  }

  :deep(.van-tabs__line) {
    display: none;
  }

  :deep(.van-tabs__content) {
    overflow: visible;
  }
}

.user-guide-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 20px;
}

.user-guide-card {
  display: flex;
  align-items: center;
  padding: 16px 16px;
  border: 1px solid #d3def4;
  border-radius: 12px;
  background: #ffffff;
  box-sizing: border-box;
}

.user-guide-card__image {
  width: 70px;
  height: 70px;
  flex: 0 0 72px;
  margin-right: 10px;
  object-fit: contain;
}

.user-guide-card__title {
  flex: 1;
  min-width: 0;
  color: #000;
  font-size: 15px;
}

.user-guide-card__arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-left: 8px;
  border-radius: 50%;
  background: #f0f1f2;
  color: #777777;

  .app-image {
    width: 20px;
    height: 20px;
    margin: 0;
  }
}

.user-guide-footer {
  flex: 0 0 auto;
  margin: 0;
  padding: 0 16px calc(26px + env(safe-area-inset-bottom));
  color: #888888;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
</style>
